import React, { useCallback, useEffect, useState } from 'react';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Button, DialogActions, DialogContent, LinearProgress, RadioGroup, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { EXCEL_EXPORT_INFO_MESSAGE } from 'common/constants/documents';
import { Logger } from 'utilities';
import { ExcelExportFormValues } from '../types';
import { BaseExcelExportDialogProps } from './BaseExcelExportDialog.props';
import {
  StyledContentContainer,
  StyledDialog,
  StyledDialogTitle,
  StyledInfoIcon,
  StyledProgressContainer,
  StyledRadioContainer,
  StyledRectangle,
  StyledSkeleton,
  StyledSkeletonContainer,
} from './BaseExcelExportDialog.styles';

export const BaseExcelExportDialog = ({
  open,
  onClose,
  title,
  selectedOption,
  setSelectedOption,
  dataLoadError,
  isLoading,
  isExportDisabled,
  isExportLoading = false,
  onExport,
  renderSubOptions,
  renderOtherOptions,
}: BaseExcelExportDialogProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [hasExportError, setHasExportError] = useState(false);
  const [wasPolling, setWasPolling] = useState(false);

  // Reset error state when dialog is closed
  useEffect(() => {
    if (!open) {
      setHasExportError(false);
      setWasPolling(false);
    }
  }, [open]);

  // Track when polling starts
  useEffect(() => {
    if (isExportLoading) {
      setWasPolling(true);
    }
  }, [isExportLoading]);

  // Close dialog when polling completes successfully (isExportLoading transitions from true to false)
  useEffect(() => {
    if (!isExportLoading && !isExporting && !hasExportError && open && wasPolling) {
      // Polling completed successfully, close the dialog
      onClose();
    }
  }, [isExportLoading, isExporting, hasExportError, open, wasPolling, onClose]);

  const handleOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedOption(event.target.value);
    },
    [setSelectedOption]
  );

  const handleExportClick = useCallback(async () => {
    // Allow retry if there's an export error, otherwise check normal conditions
    if ((isExportDisabled && !hasExportError) || isExporting || isExportLoading) {
      return;
    }

    // Clear any previous export error when starting a new export
    setHasExportError(false);
    setIsExporting(true);

    try {
      const result = onExport();
      if (result && typeof result === 'object') {
        await (result as Promise<void>);
      }
      // Dialog closing is now handled by useEffect that watches isExportLoading
    } catch (error) {
      Logger.error('Export failed:', error);
      enqueueSnackbar('Export failed. Please try again.', {
        variant: 'error',
        autoHideDuration: 5000,
      });
      // Set error state to enable retry button
      setHasExportError(true);
    } finally {
      setIsExporting(false);
    }
  }, [isExportDisabled, isExporting, isExportLoading, onExport, hasExportError]);

  const getButtonText = () => {
    if (isExporting || isExportLoading) {
      return 'Exporting...';
    }
    if (hasExportError) {
      return 'Retry Export';
    }
    return 'Export Selection';
  };

  const isButtonDisabled = isExporting || isExportLoading || (isExportDisabled && !hasExportError);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>{title}</StyledDialogTitle>
      <DialogContent>
        {(isExporting || isExportLoading) && (
          <StyledProgressContainer>
            <LinearProgress />
          </StyledProgressContainer>
        )}
        {isLoading ? (
          <StyledSkeletonContainer>
            {Array.from({ length: 4 }, (_, index) => (
              <StyledSkeleton key={index}>
                <StyledRectangle />
              </StyledSkeleton>
            ))}
          </StyledSkeletonContainer>
        ) : (
          <StyledRadioContainer>
            <RadioGroup value={selectedOption} onChange={handleOptionChange}>
              {renderSubOptions(selectedOption)}
              {renderOtherOptions(selectedOption)}
            </RadioGroup>
          </StyledRadioContainer>
        )}

        <StyledContentContainer>
          <StyledInfoIcon>
            <InfoIcon color="action" />
          </StyledInfoIcon>
          <Typography>{EXCEL_EXPORT_INFO_MESSAGE}</Typography>
        </StyledContentContainer>

        {dataLoadError && (
          <StyledContentContainer>
            <Typography color="error" variant="body2">
              {dataLoadError}. Some export options may not be available.
            </Typography>
          </StyledContentContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={handleExportClick} disabled={isButtonDisabled}>
          {getButtonText()}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export const useBaseExcelExportForm = () => {
  const [formValues, setFormValues] = useState<ExcelExportFormValues>({
    export_breakpoints: false,
    export_cap_table: false,
    export_documents: false,
    export_financials: false,
    export_firm_funds_summaries: false,
    export_firm_summary: false,
    export_fund_investments: false,
    export_fund_ownership: false,
    export_fund_summary: false,
    export_summary: false,
    export_valuations: false,
    export_waterfall: false,
    export_not_formulated: true,
    export_formulated_and_print_ready: false,
    include_documents_and_notes_from_tabs: false,
    breakpoint_cap_table: null,
    cap_table: null,
    financial_statement: null,
    fo_cap_table: null,
    valuation: null,
    waterfall_cap_table: null,
    fund_summary: null,
    fund_investments: null,
    report_date: null,
  });

  const updateFormValues = useCallback((updates: Partial<ExcelExportFormValues>) => {
    setFormValues(prev => ({ ...prev, ...updates }));
  }, []);

  return {
    formValues,
    setFormValues,
    updateFormValues,
  };
};

export const useBaseExcelExportState = () => {
  const [formulated, setFormulated] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setFormulated(true);
    setDataLoadError(null);
  }, []);

  return {
    formulated,
    setFormulated,
    isLoading,
    setIsLoading,
    dataLoadError,
    setDataLoadError,
    resetState,
  };
};
